import React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AirlineSeatReclineNormalOutlinedIcon from '@mui/icons-material/AirlineSeatReclineNormalOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InputOutlinedIcon from '@mui/icons-material/InputOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import LowPriorityOutlinedIcon from '@mui/icons-material/LowPriorityOutlined';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Divider, List, ListSubheader, Menu, Tooltip } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useCan } from '../auth/useCan';
import { config } from '../libs/config';

/**
 * The navigation rail.
 *
 * One width, icons only, and anything with children opens beside it rather than underneath -
 * the arrangement the TwinWorX dashboard design uses. There is no expanded state to switch to,
 * which is the point: a rail that is only ever one width has no second layout to get wrong.
 *
 * Described as data rather than markup. Every item needs the same four things - where it goes,
 * what it is called, which icon, and whether this user may see it - and writing that out as
 * twenty near-identical blocks is how the icon set drifted and how the route list for group
 * expansion ended up being a second copy that had to be kept in step by hand. Which group holds
 * the current page is now read off the same definition that renders it.
 *
 * Every leaf maps to a URN in rbac/esb-admin.seed.json. An item this user may not have is
 * not rendered at all.
 *
 * It used to render hidden, so that a permission changing was a style rather than a mount.
 * That was worth having when this was an expanding menu holding its own open sections -
 * remounting closed them. The rail keeps no such state, so the saving was gone and what
 * remained was a list of every screen in the console sitting in the DOM of a user who may
 * not open any of them, readable by anyone who opens devtools.
 *
 * The flyout does hold state, but it is keyed by the group rather than by the node, and a
 * permission arriving while a flyout is open is not a case worth keeping the leak for.
 */
export function AppMenuItems() {
    const { pathname } = useLocation();
    const [flyout, setFlyout] = React.useState({ anchor: null, key: null });

    const canDashboard = useCan('URN:TWX:ESB;;Dashboard', 'read').allow;
    const canServices = useCan('URN:TWX:ESB;;Services', 'read').allow;
    const canSources = useCan('URN:TWX:ESB;;Integrations:Sources', 'read').allow;
    const canRouting = useCan('URN:TWX:ESB;;Integrations:Routing', 'read').allow;
    const canAlarmPriorities = useCan('URN:TWX:ESB;;Alarms:Priorities', 'read').allow;
    const canAlarmGroups = useCan('URN:TWX:ESB;;Alarms:Groups', 'read').allow;
    const canAlarmTypes = useCan('URN:TWX:ESB;;Alarms:Types', 'read').allow;
    const canAlarmViewer = useCan('URN:TWX:ESB;;Viewers:AlarmViewer', 'read').allow;
    const canPassengers = useCan('URN:TWX:ESB;;Viewers:Passengers', 'read').allow;
    const canMessages = useCan('URN:TWX:ESB;;Viewers:Messages', 'read').allow;
    const canLogs = useCan('URN:TWX:ESB;;Viewers:Logs', 'read').allow;
    const canLookupsSystem = useCan('URN:TWX:ESB;;DataLookups:System', 'read').allow;
    const canLocations = useCan('URN:TWX:ESB;;DataLookups:Locations', 'read').allow;
    const canUsers = useCan('URN:TWX:ESB;;Users', 'read').allow;
    const canSettings = useCan('URN:TWX:ESB;;Settings', 'read').allow;

    // The passenger view and the alarm viewer are alternatives on the same /user route (see
    // App.js), so config decides which of the two rows can appear at all and the URN decides
    // whether this user sees it. The config check is needed as well as the URN because local
    // mode opens every Viewers: URN to any authenticated user (auth/urnRoles.js) — the URN
    // alone would put a Passengers row in MHAC's and NSP's menus, pointing at a screen those
    // deployments don't build.
    const isPassengerSite = config.dashboard === 'ghd';
    const showPassengers = isPassengerSite && canPassengers;
    const showAlarmViewer = !isPassengerSite && canAlarmViewer;

    const sections = [
        {
            name: 'Monitoring',
            items: [
                { label: 'Dashboard', to: '/admin', icon: <SpaceDashboardOutlinedIcon />, show: canDashboard },
                { label: 'Services', to: '/admin/service-health', icon: <MonitorHeartOutlinedIcon />, show: canServices },
                {
                    key: 'viewers', label: 'Viewers', icon: <VisibilityOutlinedIcon />,
                    children: [
                        { label: 'Passengers', to: '/user', icon: <AirlineSeatReclineNormalOutlinedIcon />, show: showPassengers },
                        { label: 'Alarms', to: '/user', icon: <WarningAmberOutlinedIcon />, show: showAlarmViewer },
                        { label: 'Messages', to: '/admin/messages', icon: <ForumOutlinedIcon />, show: canMessages },
                        { label: 'Logs', to: '/admin/logs', icon: <ArticleOutlinedIcon />, show: canLogs },
                    ],
                },
            ],
        },
        {
            name: 'Configuration',
            items: [
                {
                    key: 'integrations', label: 'Integrations', icon: <HubOutlinedIcon />,
                    children: [
                        { label: 'Sources', to: '/admin/sources', icon: <InputOutlinedIcon />, show: canSources },
                        { label: 'Routing', to: '/admin/routing', icon: <AltRouteOutlinedIcon />, show: canRouting },
                    ],
                },
                {
                    key: 'alarms', label: 'Alarms', icon: <NotificationsActiveOutlinedIcon />,
                    children: [
                        { label: 'Priorities', to: '/admin/alarmPriorities', icon: <LowPriorityOutlinedIcon />, show: canAlarmPriorities },
                        { label: 'Groups', to: '/admin/alarmGroups', icon: <GroupWorkOutlinedIcon />, show: canAlarmGroups },
                        { label: 'Types', to: '/admin/alarmTypes', icon: <CategoryOutlinedIcon />, show: canAlarmTypes },
                    ],
                },
                {
                    key: 'lookups', label: 'Data Lookups', icon: <MenuBookOutlinedIcon />,
                    children: [
                        { label: 'System', to: '/admin/lookups', icon: <DataObjectOutlinedIcon />, show: canLookupsSystem },
                        { label: 'Locations', to: '/admin/locations', icon: <PlaceOutlinedIcon />, show: canLocations },
                    ],
                },
            ],
        },
        {
            name: 'Administration',
            items: [
                { label: 'Users', to: '/admin/users', icon: <PeopleOutlinedIcon />, show: canUsers },
                { label: 'Settings', to: '/admin/settings', icon: <SettingsOutlinedIcon />, show: canSettings },
            ],
        },
    ];

    /*
     * "/admin" is matched exactly. As a prefix it is the parent of every other route here and
     * would mark the dashboard on every page. The rest match by prefix so a detail page keeps
     * its section marked - routing details stays under Routing rather than clearing the rail
     * while you are looking at it.
     */
    const isAt = (path) => path === '/admin' ? pathname === '/admin' : pathname.startsWith(path);

    // A group has no route of its own, so it is current when one of its children is.
    const visibleChildren = (item) => (item.children ?? []).filter((child) => child.show);
    const isCurrent = (item) => item.to
        ? isAt(item.to)
        : visibleChildren(item).some((child) => isAt(child.to));

    // A group with nothing under it this user may see is not a dead row, it is not a row.
    const isVisible = (item) => item.children ? visibleChildren(item).length > 0 : item.show;

    /*
     * The same question asked of a whole section.
     *
     * A restricted user can empty one outright - a viewer with no configuration rights has
     * nothing in Configuration and nothing in Administration - and the rule was drawn on the
     * section's index, whatever was or was not under it. That left a rule with nothing above
     * it to separate from what is below, and two empty sections in a row left two rules
     * stacked together.
     */
    const sectionFilled = sections.map((section) => section.items.some(isVisible));
    const anyItems = sectionFilled.some(Boolean);

    // A rule is only a grouping if there is something on both sides of it.
    const needsRule = (index) =>
        sectionFilled[index] && sectionFilled.slice(0, index).some(Boolean);

    /*
     * A group with one child left is not a group.
     *
     * The rail is icons only, so a group is an icon that opens a list - and a list of one is a
     * detour: two clicks and a panel to reach something the rail had room to offer directly.
     * Worse, the icon shown is the group's, so a user whose only configuration right is
     * Sources sees a Data Lookups book or an Integrations hub and has no way of knowing what
     * is behind it without opening it.
     *
     * Restricted users are who this happens to, and they are the ones with the least context
     * for guessing. So the child takes the place: its icon, its name, its route.
     *
     * The group's key is kept rather than the child's, so that a permission arriving or
     * leaving swaps the contents of this position rather than replacing the element in it -
     * the same reason the items hide rather than unmount.
     */
    const effective = (item) => {
        if (!item.children) {
            return item;
        }

        const visible = visibleChildren(item);
        return visible.length === 1 ? { ...visible[0], key: item.key } : item;
    };

    const openFlyout = (key) => (event) => setFlyout({ anchor: event.currentTarget, key });
    const closeFlyout = () => setFlyout({ anchor: null, key: null });

    const flyoutItems = sections
        .flatMap((section) => section.items)
        .find((item) => item.key === flyout.key);

    /*
     * A function that returns elements, not a component defined in the render body.
     *
     * As a component it would be a brand-new type on every render, and React compares element
     * types by identity - so a new type in the same position unmounts the old subtree and mounts
     * a fresh one rather than updating it. The rail would survive that; the flyout does not. It
     * holds the DOM node it was opened from, and opening it is a state change, so the node it
     * held was detached before the menu measured it - leaving the menu to fall back on the
     * viewport corner, sixteen pixels from the top, nowhere near the icon pressed.
     *
     * This is the same mistake the comment at the top of layout.js describes, which is what a
     * styled() in a render body did to this very menu.
     */
    const renderRailItem = (item) => (
        <Tooltip key={item.key ?? item.to} title={item.label} placement="right" arrow>
            <ListItemButton
                {...(item.to
                    ? { component: Link, to: item.to }
                    : { onClick: openFlyout(item.key), 'aria-haspopup': 'true' })}
                selected={isCurrent(item)}
                sx={{
                    justifyContent: 'center',
                    px: 0,
                    py: 1.25,
                }}
            >
                {/* No label beside it, so the icon is the whole control and centres itself. */}
                <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                    {item.icon}
                </ListItemIcon>
            </ListItemButton>
        </Tooltip>
    );

    return (
        <>
            <List component="nav" aria-label="main menu" sx={{ px: 0 }}>
                {sections.map((section, index) => (
                    <React.Fragment key={section.name}>
                        {/*
                          * A rule rather than a heading. The sections still group the rail, but a
                          * rail this narrow has nowhere to write "Configuration" - so the break
                          * between one run and the next is drawn instead of named, and the names
                          * survive only as the tooltips on the items themselves.
                          */}
                        {index > 0 && needsRule(index) && <Divider sx={{ my: 1 }} />}
                        {section.items
                            .map(effective)
                            .filter(isVisible)
                            .map(renderRailItem)}
                    </React.Fragment>
                ))}
            </List>

            {/*
              * Children open beside the rail rather than underneath it. Underneath is what the
              * wider rail did, and it cannot work here: the children would be a second column of
              * unlabelled icons indistinguishable from their parents. Beside it they have room
              * for their names.
              */}
            <Menu
                anchorEl={flyout.anchor}
                open={Boolean(flyout.anchor)}
                onClose={closeFlyout}
                /*
                 * Opens level with the icon that opened it, which needs saying explicitly.
                 * A Menu defaults to variant="selectedMenu", which positions its selected item
                 * over the anchor rather than its top edge - so on a page whose group is open,
                 * the flyout slid up by however many items sat above the current one and
                 * appeared beside a different icon than the one just pressed.
                 */
                variant="menu"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                MenuListProps={{ dense: true, sx: { minWidth: 200 } }}
                slotProps={{ paper: { sx: { ml: 1 } } }}
            >
                <ListSubheader component="div" sx={{ bgcolor: 'transparent', lineHeight: 2.2 }}>
                    {flyoutItems?.label}
                </ListSubheader>

                {visibleChildren(flyoutItems ?? {}).map((child) => (
                    <ListItemButton
                        key={child.label}
                        component={Link}
                        to={child.to}
                        selected={isAt(child.to)}
                        onClick={closeFlyout}
                    >
                        <ListItemIcon>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.label} />
                    </ListItemButton>
                ))}
            </Menu>

            {/*
              * Signing out is a way out rather than another destination, so a rule separates it
              * from the pages - but it stays with them rather than being pushed to the floor of
              * the window.
              *
              * It was pinned down there, which reads correctly at a site whose mark sits beneath
              * it and stranded at one whose does not. Only MHAC mounts a mark; Oriel, NSP and GHD
              * mount none, so on three of the four sites it was a lone icon at the bottom of an
              * otherwise empty rail. The mark keeps the foot, and takes the empty space with it.
              */}
            <List component="nav" dense sx={{ px: 0 }}>
                {anyItems && <Divider sx={{ mb: 1 }} />}

                <Tooltip title="Sign Out" placement="right" arrow>
                    <ListItemButton
                        component={Link}
                        to={"/signout"}
                        sx={{ justifyContent: 'center', px: 0, py: 1.25 }}
                    >
                        <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                            <LogoutOutlinedIcon />
                        </ListItemIcon>
                    </ListItemButton>
                </Tooltip>
            </List>
        </>
    );
}

/** Kept for the flyout's affordance, so a group reads as having more behind it. */
export const FlyoutHint = ChevronRightIcon;
